import React, { useMemo } from "react";
import Select from "react-dropdown-select";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { Address } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import { FormRow, FormField, FormLabel, FormInput, FormSelect, SearchableSelectWrapper } from "@/screen/Identities/IdentityAddressInputs/Styles/IdentityAddressInputsStyles";
import AddressTypes from "@/utils/AddressTypes";
import { useAddressCascade, CascadeOption } from "@/screen/Identities/IdentityAddressInputs/hooks/useAddressCascade";
import { highlightAddressSubfield } from "@/screen/Identities/IdentityForm/pendingFieldHighlight";
import { resolveRoadTypeCode } from "@/screen/Identities/IdentityForm/identityAddressValidation";

export interface AddressSectionInputsProps
{
    prefix: string;
    address: Address | null;
    isFormLocked: boolean;
    onChange: (field: string, value: string) => void;
    highlightPendingManualFields: boolean;
    sectionAddressComplete: boolean;
    onLoadingChange: (loading: boolean) => void;
}

const AddressSectionInputs: React.FC<AddressSectionInputsProps> = ({prefix, address, isFormLocked, onChange, highlightPendingManualFields, sectionAddressComplete, onLoadingChange}) =>
{
    const {regions, cities, postalCodes, loadingCities, loadingPostalCodes, onRegionChange, onCityChange} = useAddressCascade(address?.state, address?.city);
    const isCascadeLoading = loadingCities || loadingPostalCodes;

    React.useEffect(() =>
    {
        onLoadingChange(isCascadeLoading);
    }, [isCascadeLoading, onLoadingChange]);

    // Autocuración de datos heredados: algunos registros guardaron en `city` el ObjectId de la ciudad
    // de Finametrix (24 hex) en lugar del nombre. El desplegable lo resuelve por id y muestra el nombre
    // correcto, de modo que el usuario no lo vuelve a tocar y, al guardar, ese ObjectId se re-persistía
    // intacto. En cuanto tenemos la lista de ciudades resuelta, si el valor actual casa con una ciudad 
    // por id (y no ya por su nombre), lo reescribimos a su literal para que el guardado (aunque el campo
    // no se toque) viaje siempre con el nombre. Es idempotente: una vez es el nombre, deja de casar por id
    // y no reentra.
    React.useEffect(() =>
    {
        const cityValue = address?.city;
        if(!cityValue || cities.length === 0)
        {
            return;
        }

        const matchById = cities.find((cityOption) => cityOption.id === cityValue);
        if(matchById && matchById.text !== cityValue)
        {
            onChange("city", matchById.text);
        }
    }, [address?.city, cities, onChange]);

    const ph = (field: keyof Address): boolean => highlightAddressSubfield(highlightPendingManualFields, sectionAddressComplete, field, address);
    const selectedRegionValue = (() =>
    {
        const regionValue = address?.state || "";
        if(!regionValue)
        {
            return "";
        }

        const exactByText = regions.find((regionOption) => regionOption.text === regionValue);
        if(exactByText)
        {
            return exactByText.text;
        }

        const exactById = regions.find((regionOption) => regionOption.id === regionValue);
        if(exactById)
        {
            return exactById.text;
        }

        return regionValue;
    })();

    const selectedCityOption = useMemo<CascadeOption[]>(() =>
    {
        const cityValue = address?.city || "";
        if(!cityValue)
        {
            return [];
        }

        const exactById = cities.find((cityOption) => cityOption.id === cityValue);
        if(exactById)
        {
            return [exactById];
        }

        const normalizedCityValue = cityValue.trim().toLowerCase();
        const matchByText = cities.find((cityOption) => cityOption.text.trim().toLowerCase() === normalizedCityValue);
        if(matchByText)
        {
            return [matchByText];
        }

        return [];
    }, [address?.city, cities]);

    const selectedZipCodeOption = useMemo<CascadeOption[]>(() =>
    {
        const zipCodeValue = address?.zipCode || "";
        if(!zipCodeValue)
        {
            return [];
        }

        const exactById = postalCodes.find((zipOption) => zipOption.id === zipCodeValue);
        if(exactById)
        {
            return [exactById];
        }

        const normalizedZipCodeValue = zipCodeValue.trim().toLowerCase();
        const matchByText = postalCodes.find((zipOption) => zipOption.text.trim().toLowerCase() === normalizedZipCodeValue);
        if(matchByText)
        {
            return [matchByText];
        }

        return [];
    }, [address?.zipCode, postalCodes]);

    const handleRegionChange = (regionText: string) =>
    {
        onChange("state", regionText);
        onChange("city", "");
        onChange("zipCode", "");
        onRegionChange(regionText);
    };

    // La ciudad se PERSISTE por su literal (nombre), no por el id de Finametrix: el id (ObjectId) solo
    // se usa internamente para encadenar la carga de códigos postales. Guardar el id metía un
    // NextPortfolioId en app.Address.City en lugar del nombre. El código postal ya es literal por sí mismo.
    const handleCityChange = (cityName: string, cityId: string) =>
    {
        onChange("city", cityName);
        onChange("zipCode", "");
        onCityChange(cityId);
    };

    const handleZipChange = (zipId: string) =>
    {
        onChange("zipCode", zipId);
    };

    return(
        <React.Fragment>
            <FormRow>
                <FormField>
                    <FormLabel required>Tipo de Vía</FormLabel>
                    {/* Se muestra la opción equivalente a la grafía guardada ("Calle", "URBN"...); el valor
                        guardado solo cambia si el usuario elige otra opción. */}
                    <FormSelect name={`${prefix}_roadType`}
                        value={resolveRoadTypeCode(address?.roadType) ?? ""}
                        onChange={(e) => onChange("roadType", e.target.value)}
                        disabled={isFormLocked}
                        $pendingHighlight={ph("roadType")}
                    >
                        <option value="">-- Seleccionar --</option>
                        {AddressTypes.map((t) =>
                        (
                            <option key={t.value} value={t.value}>{t.text}</option>
                        ))}
                    </FormSelect>
                </FormField>
                <FormField>
                    <FormLabel required>Nombre de Vía</FormLabel>
                    <FormInput type="text" name={`${prefix}_streetName`}
                        value={address?.streetName || ""}
                        onChange={(e) => onChange("streetName", e.target.value)}
                        placeholder="Mayor"
                        disabled={isFormLocked}
                        autoComplete="off"
                        $pendingHighlight={ph("streetName")}
                    />
                </FormField>
                <FormField>
                    <FormLabel required>Número</FormLabel>
                    {/* Texto libre: el número de vía puede ser alfanumérico (p. ej. "28A"). Con
                        type="number" el navegador descarta esos valores y el campo llega vacío,
                        invalidando la dirección y bloqueando el guardado. */}
                    <FormInput type="text" name={`${prefix}_streetNumber`}
                        value={address?.streetNumber || ""}
                        onChange={(e) => onChange("streetNumber", e.target.value)}
                        placeholder="10"
                        disabled={isFormLocked}
                        autoComplete="off"
                        $pendingHighlight={ph("streetNumber")}
                    />
                </FormField>
            </FormRow>
            <FormRow>
                <FormField>
                    <FormLabel>Piso</FormLabel>
                    <FormInput type="text" name={`${prefix}_streetFloor`}
                        value={address?.streetFloor || ""}
                        onChange={(e) => onChange("streetFloor", e.target.value)}
                        placeholder="2"
                        disabled={isFormLocked}
                        autoComplete="off"
                    />
                </FormField>
                <FormField>
                    <FormLabel>Escalera</FormLabel>
                    <FormInput type="text" name={`${prefix}_streetStair`}
                        value={address?.streetStair || ""}
                        onChange={(e) => onChange("streetStair", e.target.value)}
                        placeholder="A"
                        disabled={isFormLocked}
                        autoComplete="off"
                    />
                </FormField>
                <FormField>
                    <FormLabel>Puerta</FormLabel>
                    <FormInput type="text" name={`${prefix}_streetDoor`}
                        value={address?.streetDoor || ""}
                        onChange={(e) => onChange("streetDoor", e.target.value)}
                        placeholder="1A"
                        disabled={isFormLocked}
                        autoComplete="off"
                    />
                </FormField>
                <FormField>
                    <FormLabel>Otros Datos</FormLabel>
                    <FormInput type="text" name={`${prefix}_streetOther`}
                        value={address?.streetOther || ""}
                        onChange={(e) => onChange("streetOther", e.target.value)}
                        placeholder="Información adicional"
                        disabled={isFormLocked}
                        autoComplete="off"
                    />
                </FormField>
            </FormRow>
            <FormRow>
                <FormField>
                    <FormLabel required>Provincia</FormLabel>
                    <FormSelect name={`${prefix}_state`}
                        value={selectedRegionValue}
                        onChange={(e) => handleRegionChange(e.target.value)}
                        disabled={isFormLocked}
                        $pendingHighlight={ph("state")}
                    >
                        <option value="">-- Seleccionar --</option>
                        {regions.map((r) =>
                        (
                            <option key={r.id} value={r.text}>{r.text}</option>
                        ))}
                    </FormSelect>
                </FormField>
                <FormField>
                    <FormLabel required>Ciudad</FormLabel>
                    {loadingCities ?
                    (
                        <Spinner size={SpinnerSize.small} />
                    ) :
                    (
                        <SearchableSelectWrapper $pendingHighlight={ph("city")} $disabled={isFormLocked || cities.length === 0}>
                            <Select<CascadeOption>
                                options={cities}
                                values={selectedCityOption}
                                onChange={(selected) => handleCityChange(selected[0]?.text ?? "", selected[0]?.id ?? "")}
                                placeholder="-- Seleccionar --"
                                searchable
                                clearable={false}
                                dropdownPosition="bottom"
                                dropdownHeight="260px"
                                labelField="text"
                                valueField="id"
                                searchBy="text"
                                disabled={isFormLocked || cities.length === 0}
                                name={`${prefix}_city`}
                                noDataLabel="No hay resultados"
                                className="identity-city-select"
                            />
                        </SearchableSelectWrapper>
                    )}
                </FormField>
                <FormField>
                    <FormLabel required>Código Postal</FormLabel>
                    {loadingPostalCodes ?
                    (
                        <Spinner size={SpinnerSize.small} />
                    ) :
                    (
                        <SearchableSelectWrapper $pendingHighlight={ph("zipCode")} $disabled={isFormLocked || postalCodes.length === 0}>
                            <Select<CascadeOption>
                                options={postalCodes}
                                values={selectedZipCodeOption}
                                onChange={(selected) => handleZipChange(selected[0]?.id ?? "")}
                                placeholder="-- Seleccionar --"
                                searchable
                                clearable={false}
                                dropdownPosition="bottom"
                                dropdownHeight="260px"
                                labelField="text"
                                valueField="id"
                                searchBy="text"
                                disabled={isFormLocked || postalCodes.length === 0}
                                name={`${prefix}_zipCode`}
                                noDataLabel="No hay resultados"
                                className="identity-zipcode-select"
                            />
                        </SearchableSelectWrapper>
                    )}
                </FormField>
            </FormRow>
        </React.Fragment>
    );
};

export default AddressSectionInputs;